import { useState, useEffect } from "react";
import { Menu, X, Phone, MessageSquare } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const Navigation = ({ onContactClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#about", label: "About" },
    { href: "#gallery", label: "Gallery" },
    { href: "#testimonials", label: "Testimonials" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <header
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "nav-sticky shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#"
            data-testid="logo-link"
            className="flex items-center gap-3"
          >
            <img 
              src={isScrolled 
                ? "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/czouo4lo_wa-logo.png"
                : "https://customer-assets.emergentagent.com/job_joinery-landing/artifacts/nlxukwpp_wa-logo-white.png"
              }
              alt="J. Antrobus & Son"
              className="h-14 w-auto"
            />
            <span className={`font-heading font-bold text-lg tracking-tight hidden sm:block ${isScrolled ? 'text-slate-900' : 'text-white'}`}>
              J. Antrobus & Son
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`font-body text-sm font-medium transition-colors hover:text-royal ${
                  isScrolled ? "text-slate-600" : "text-white/90"
                }`}
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={onContactClick}
              data-testid="nav-get-quote"
              className={`flex items-center gap-2 px-4 py-2 font-mono text-sm font-medium transition-colors ${
                isScrolled 
                  ? "bg-royal text-white hover:bg-royal/90" 
                  : "bg-white text-slate-900 hover:bg-slate-100"
              }`}
            >
              <MessageSquare size={16} />
              <span>Get Quote</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 ${isScrolled ? "text-slate-900" : "text-white"}`}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-slate-200"
            >
              <div className="py-4 space-y-2">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-3 text-slate-700 font-medium hover:bg-slate-50 hover:text-royal"
                  >
                    {link.label}
                  </a>
                ))}
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    onContactClick();
                  }}
                  className="block w-full text-left px-4 py-3 text-royal font-medium bg-royal/10"
                >
                  <MessageSquare size={16} className="inline mr-2" />
                  Get Quote
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
